import React, { useState, useEffect, useCallback, useRef } from 'react';

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  onSearch: (query: string) => void;
  onImageUpload?: (file: File) => void;
  imagePreviewUrl?: string | null;
  onClearSearch?: () => void;
  placeholder?: string;
  isLoading?: boolean;
  isImageLoading?: boolean;
}

export default function SearchBar({
  value,
  onChange,
  onSearch,
  onImageUpload,
  imagePreviewUrl = null,
  onClearSearch,
  placeholder = '输入卡片名称或编号...',
  isLoading = false,
  isImageLoading = false,
}: SearchBarProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);
  const hasImage = Boolean(imagePreviewUrl);
  const handleClear = useCallback(() => {
    if (onClearSearch) {
      onClearSearch();
    } else {
      onChange('');
    }
  }, [onClearSearch, onChange]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasImage && value.trim()) {
      onSearch(value.trim());
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (hasImage) return;
    if (e.key === 'Enter' && value.trim()) {
      onSearch(value.trim());
    }
  };

  const handleCameraClick = () => {
    setIsExpanded(true);
  };

  const handleClose = useCallback(() => {
    setIsExpanded(false);
    setIsDragging(false);
  }, []);

  const handleImageUpload = useCallback(
    (file: File) => {
      // 移除输入框焦点，隐藏光标
      inputRef.current?.blur();
      onImageUpload?.(file);
      handleClose();
    },
    [onImageUpload, handleClose]
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
    e.target.value = '';
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleCameraInputClick = () => {
    cameraInputRef.current?.click();
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const file = e.dataTransfer.files?.[0];
      if (file && file.type.startsWith('image/')) {
        handleImageUpload(file);
      }
    },
    [handleImageUpload]
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  // 点击外部关闭
  useEffect(() => {
    if (!isExpanded) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        handleClose();
      }
    };

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isExpanded, handleClose]);

  // 全局拖放监听
  useEffect(() => {
    if (!onImageUpload) return;

    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      dragCounterRef.current++;
      if (e.dataTransfer?.types.includes('Files')) {
        setIsExpanded(true);
      }
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      dragCounterRef.current--;
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      dragCounterRef.current = 0;
    };

    document.addEventListener('dragenter', handleDragEnter);
    document.addEventListener('dragleave', handleDragLeave);
    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('drop', handleDrop);

    return () => {
      document.removeEventListener('dragenter', handleDragEnter);
      document.removeEventListener('dragleave', handleDragLeave);
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('drop', handleDrop);
    };
  }, [onImageUpload]);

  // 渲染搜索框内容（复用于普通状态和展开状态）
  const renderSearchInput = () => (
    <div className="relative flex items-center">
      {/* 搜索图标 */}
      <div className="absolute left-4">
        {hasImage ? (
          <img
            src={imagePreviewUrl || undefined}
            alt="搜索图片"
            className="w-9 h-9 rounded-md object-cover border border-border"
          />
        ) : (
          <div className="text-secondary-light">
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        )}
      </div>

      {/* 输入框 */}
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={hasImage ? '已选择图片' : placeholder}
        disabled={isLoading || hasImage}
        className={`w-full ${hasImage ? 'pl-14' : 'pl-12'} pr-32 py-3 bg-transparent focus:outline-none transition-all text-primary placeholder-secondary-light`}
      />

      {/* 相机按钮 / 清除按钮 */}
      {onImageUpload && (
        hasImage ? (
          <button
            type="button"
            onClick={handleClear}
            disabled={!onClearSearch || isImageLoading}
            className="absolute right-20 p-2 text-secondary hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="清除图片"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        ) : value.trim() ? (
          // 有输入内容时显示清除按钮
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-20 p-2 text-secondary hover:text-primary transition-colors"
            title="清除输入"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        ) : (
          // 无输入内容时显示相机按钮
          <button
            type="button"
            onClick={handleCameraClick}
            disabled={isImageLoading}
            className="absolute right-20 p-2 text-secondary hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="通过图片搜索"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </button>
        )
      )}

      {/* 搜索按钮 */}
      <button
        type="submit"
        disabled={isLoading || !value.trim() || hasImage}
        className="absolute right-2 px-4 py-1.5 bg-primary text-white text-sm font-medium rounded-full hover:bg-primary-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {isLoading ? '搜索中...' : '搜索'}
      </button>
    </div>
  );

  return (
    <div ref={containerRef} className="relative w-full">
      {/* 隐藏的文件输入 */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        className="hidden"
      />

      {/* 普通搜索框 - 用于占位和未展开时显示 */}
      <div className="bg-white rounded-full shadow-sm hover:shadow-md border border-border">
        <form onSubmit={handleSubmit} className="w-full">
          {renderSearchInput()}
        </form>
      </div>

      {/* 展开的弹出框 - 从搜索框上沿位置开始，完全覆盖搜索框 */}
      {isExpanded && onImageUpload && (
        <div className="absolute left-0 right-0 top-0 z-50 bg-white rounded-2xl shadow-xl border border-border">
          {/* 标题栏 */}
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-base font-medium text-primary">通过图片搜索</span>
            <button
              onClick={handleClose}
              className="text-secondary hover:text-primary transition-colors p-1"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* 拖放区域 */}
          <div className="px-4 pb-4">
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                isDragging
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-secondary'
              }`}
            >
              {/* 图标 */}
              <div className="flex justify-center mb-3">
                <div className="w-12 h-12 rounded-full bg-background-secondary flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-secondary"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              </div>

              <p className="text-secondary text-sm mb-1">将图片拖到此处</p>
              <p className="text-secondary-light text-xs mb-4">或者</p>

              {/* 操作按钮 */}
              <div className="flex justify-center gap-3">
                <button
                  type="button"
                  onClick={handleUploadClick}
                  disabled={isImageLoading}
                  className="px-5 py-2 bg-primary text-white text-sm rounded-lg hover:bg-primary-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  {isImageLoading ? '上传中...' : '上传文件'}
                </button>

                {/* 移动端拍照按钮 */}
                <button
                  type="button"
                  onClick={handleCameraInputClick}
                  disabled={isImageLoading}
                  className="sm:hidden px-5 py-2 bg-white border border-border text-primary text-sm rounded-lg hover:bg-background-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                >
                  拍照
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
